import { getApiClient } from "@src/api";
import {
	invalidateNavbarBranchSnapshots,
	optimisticallyRemoveNavbarItem,
	restoreNavbarBranchSnapshots,
} from "@src/components/board/mutations/navbar-optimistic";
import { navbarBranchQueryKeys } from "@src/components/board/queries/fetch-ranklist";
import { trashQueryKeys } from "@src/components/board/queries/query-keys";
import { parseCardDocumentId } from "@src/components/collaboration/document-id/factories";
import { libraryQueryKeys } from "@src/components/library/queries/query-keys";
import { graphql } from "@src/gql/gql";
import type { NavbarSection } from "@src/gql/graphql";
import { handleError } from "@src/utils/error-handling";
import { showSuccessNotification } from "@src/utils/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const navbarRemoveCardMutation = graphql(/* GraphQL */ `
	mutation navbarRemoveCard(
		$section: NavbarSection!
		$cardId: String!
		$waitForProjections: Boolean!
	) {
		navbar {
			removeCard(
				section: $section
				cardId: $cardId
				waitForProjections: $waitForProjections
			)
		}
	}
`);

type NavbarRemoveCardInput = {
	section: NavbarSection;
	cardId: string;
	waitForProjections?: boolean;
};

/**
 * Remove a card link from a navbar section (My Workspace).
 * GraphQL: `navbar.removeCard`.
 */
export function useNavbarRemoveCard(showNotification = true) {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			section,
			cardId,
			waitForProjections,
		}: NavbarRemoveCardInput) => {
			const response = await client.mutate({
				mutation: navbarRemoveCardMutation,
				variables: {
					section,
					cardId: parseCardDocumentId(cardId).toIdString(),
					waitForProjections: waitForProjections ?? false,
				},
			});
			return response.data?.navbar?.removeCard ?? false;
		},
		onMutate: async ({ cardId }) => {
			const snapshots = await optimisticallyRemoveNavbarItem(
				queryClient,
				cardId,
			);
			return { snapshots };
		},
		onSuccess: () => {
			if (showNotification) {
				showSuccessNotification("Success", "Card removed from workspace");
			}
		},
		onError: (error, _vars, context) => {
			if (context?.snapshots) {
				restoreNavbarBranchSnapshots(queryClient, context.snapshots);
			}
			handleError(error, "Failed to remove card from workspace");
		},
		onSettled: (_data, _error, _vars, context) => {
			invalidateNavbarBranchSnapshots(queryClient, context?.snapshots ?? []);
			void queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.myCardLibrary.all,
			});
			void queryClient.invalidateQueries({
				queryKey: trashQueryKeys.all(),
			});
		},
	});
}
